import { Home, Compass, BookOpen, Users, Briefcase, HandHelping, BarChart3, PenSquare, Globe2, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = '/assets/logo.jpg';

const navItems = [
  { label: 'Feed', href: '/', icon: Home },
  { label: 'Discover', href: '/discover', icon: Compass },
  { label: 'Stories', href: '/stories', icon: BookOpen },
  { label: 'Communities', href: '/communities', icon: Users },
  { label: 'Projects', href: '/explore', icon: Briefcase },
  { label: 'Volunteer', href: '/volunteer', icon: HandHelping },
  { label: 'Impact Map', href: '/map', icon: Globe2 },
  { label: 'My Impact', href: '/dashboard', icon: BarChart3 },
];

export default function Sidebar({ onCreatePost }) {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <aside className="hidden lg:flex flex-col w-[240px] h-screen sticky top-0 border-r border-slate-200 bg-white py-5 px-3 shrink-0" data-testid="sidebar">
      <Link to="/" className="flex items-center gap-2.5 px-3 mb-6" data-testid="sidebar-logo">
        <img src={LOGO_URL} alt="HumaClub" className="h-8 w-auto rounded" />
        <span className="font-bold text-lg text-slate-900 tracking-tight">HumaClub</span>
      </Link>

      <nav className="flex-1 space-y-0.5">
        {navItems.map(item => {
          const active = location.pathname === item.href || (item.href === '/' && location.pathname === '/');
          return (
            <Link
              key={item.label}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-slate-100 text-[#7C3AED] font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className={`w-5 h-5 ${active ? 'text-[#7C3AED]' : 'text-slate-400'}`} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto space-y-3 pt-4 border-t border-slate-100">
        <button
          onClick={onCreatePost}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-200 transition-all"
          data-testid="sidebar-create-post"
        >
          <PenSquare className="w-5 h-5" />
          Create Post
        </button>

        <a
          href="https://humadashportal.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-[#7C3AED] transition-colors"
          data-testid="sidebar-humadash-link"
        >
          <ExternalLink className="w-4 h-4" />
          Open HumaDash Portal
        </a>

        {user && (
          <Link to={`/profile/${user.username}`} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center text-white text-xs font-bold shrink-0">
              {user.initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-400 truncate">{user.email}</p>
            </div>
          </Link>
        )}
      </div>
    </aside>
  );
}
